/** @format */

/**
 * M-ACCESSで使用するルートの一覧
 */

import {createBrowserRouter, Navigate, Outlet} from 'react-router-dom';
import type {LoaderFunction} from 'react-router-dom';

//MTH component import
import Root, {loader as rootLoader} from './Root';
import Login from './login/Login';
import Manual from './help/Manual';
import TermsOfService from './help/TermsOfService';
import PrivacyPolicy from './help/PrivacyPolicy';
import MainMySite from './my-site/MainMySite';
import MySiteArea from './my-site/MySiteArea';
import MainSiteDetail, {
  loader as mainSiteDetailLoader,
} from './my-site/MainSiteDetail';
import {
  GroupDetailPopOut as HCCVQGroupDetailPopOut,
  GroupDetailPopOutLoader as HCCVQGroupDetailLoader,
} from './my-site/HCCVQGroupDetail';
//ユーザ関連
import RegisterAccount from './register-account/RegisterAccount';
import ResetPassword from './reset-password/ResetPassword';
import ChangeEmail from './account-setting/ChangeEmail';
import {default as ChangePassword} from './account-setting/ChangePassword';
import {default as Notifications} from './account-setting/Notifications';
import DisplaySetting, {
  loader as displaySettingLoader,
} from './account-setting/DisplaySetting';
import DeleteAccount from './account-setting/DeleteAccount';
//サイト関連
import SiteSecurity, {loader as securityLoader} from './security/SiteSecurity';
import AddSite from './site-setting/AddSite';
import SiteSetting, {
  loader as siteSettingLoader,
} from './site-setting/SiteSetting';
//ゲートウェイ関連
import GWMain, {loader as gwLoader} from './gw-registration/GWMain';
import GWRegistration from './gw-registration/GWRegistration';
import Communication, {
  loader as communicationLoader,
} from './communication/Communication';
import OURegistration, {
  loader as ouRegistrationLoader,
} from './sl-ou-registration/OURegistration';
import IURegistration, {
  loader as iuRegistrationLoader,
} from './sl-iu-registration/IURegistration';
import OtherRegistration, {
  loader as otherRegistrationLoader,
} from './sl-other-registration/OtherRegistration';
import RTRegistration, {
  loader as rtRegistrationLoader,
} from './rt-registration/RTRegistration';
import RTIURegistration, {
  loader as rtIURegistrationLoader,
} from './rt-iu-registration/IURegistration';
import {
  default as ModbusOURegistration,
  loader as modbusOURegistrationLoader,
} from './modbus-ou-registration/OURegistration';
import {
  default as ModbusIURegistration,
  loader as modbusIURegistrationLoader,
} from './modbus-iu-registration/IURegistration';
import {
  default as CollectiveSettingDifference,
  loader as collectiveSettingDifferenceLoader,
} from './collective-setting/CollectiveSettingDifference';
//グループ関連
import FloorMain, {
  loader as floorMainLoader,
} from './floor-registration/FloorMain';
import FloorRegistration from './floor-registration/FloorRegistration';
import GroupRegistration from './group-registration/GroupRegistration';
import GroupLayoutRegistration, {
  loader as groupLayoutRegistrationLoader,
} from './group-layout-registration/GroupLayoutRegistration';
import {
  default as HCCVQGroupMain,
  loader as hccvqGroupRegistrationLoader,
} from './group-registration/HCCVQGroupMain';
import {default as HCCVQGroupRegistration} from './group-registration/HCCVQGroupRegistration';
import {
  default as ModbusGroupLayoutRegistration,
  loader as ModbusGroupLayoutRegistrationLoader,
} from './hccvq-group-layout-registration/GroupLayoutRegistration';
//サービス情報関連
import DailyReport, {
  loader as dailyReportLoader,
} from './service-info/DailyReport';
import PeriodicReport, {
  loader as PeriodicReportLoader,
} from './service-info/PeriodicReport';
import ConditionHistory, {
  loader as conditionHistoryLoader,
} from './service-info/ConditionHistory';
import RequestReport, {
  loader as requestReportLoader,
} from './service-info/RequestReport';
import PeriodicReportSetting, {
  loader as periodicReportSettingLoader,
} from './service-info/PeriodicReportSetting';
import ServiceHistory, {
  loader as ServiceHistoryLoader,
} from './service-info/ServiceHistory';
import OperationDataHistory, {
  loader as operationDataHistoryLoader,
} from './service-info/OperationDataHistory';
//スケジュール関連
import ScheduleSetting, {
  loader as ScheduleSettingLoader,
} from './schedule-setting/ScheduleSetting';
import TodaySchedule, {
  loader as TodayScheduleLoader,
} from './schedule-setting/TodaySchedule';
import DailySchedule, {
  loader as DailyScheduleLoader,
} from './schedule-setting/ScheduleTemplate';
//管理関連
import DemandControl, {
  loader as DemandLoader,
} from './demand-control/DemandControl';
import Logic, {loader as logicLoader} from './logic/Logic';
import SavingEnergyMain, {
  loader as savingEnergyMainLoader,
} from './saving-energy/SavingEnergyMain';
import SavingEnergyDetail, {
  loader as savingEnergyLoader,
} from './saving-energy/SavingEnergy';
//課金関連
import BillingSetting from './billing-setting/BillingSetting';

/**
 * パスとコンポーネント一覧
 */
type typeRouteList = {
  /**  大文字禁止・2単語以上使う場合は「-」で結ぶ（パスパラメータを除く） */
  path: string;
  element: JSX.Element;
  loader?: LoaderFunction;
};
export const routeList: {[key: string]: typeRouteList} = {
  //ルート
  root: {
    path: '/',
    element: <Root />,
    loader: rootLoader,
  },
  //ポップアウト
  popOut: {
    path: '/pop-out',
    element: <Root isPopOut={true} />,
    loader: rootLoader,
  },
  //ログイン
  login: {
    path: '/',
    element: <Login />,
  },
  //新規ユーザ登録
  registerAccount: {
    path: '/pages/register-account',
    element: <RegisterAccount />,
  },
  //ユーザ登録時用ユーザ規約
  registerAccountTermsOfService: {
    path: '/pages/register-account/terms-of-service',
    element: (
      <TermsOfService isAuthenticated={false} isRegisteringUser={true} />
    ),
  },
  //パスワードリセット
  resetPassword: {
    path: '/pages/reset-password',
    element: <ResetPassword />,
  },
  //マイサイト
  mySite: {
    path: '/pages/my-site',
    element: <MySiteArea />,
  },
  //サイト詳細（サイトの状態値）
  siteDetail: {
    path: '/pages/my-site/site-detail/:siteID/state',
    element: <MySiteArea />,
    loader: mainSiteDetailLoader, //サブメニューのLoader
  },
  //サイト詳細（状態値を表示するページを除く）
  siteDetail2: {
    path: '/pages/my-site/site-detail/:siteID',
    element: <></>,
  },
  //サイト詳細（グループ、空調機の状態値）
  targetDetail: {
    path: '/pages/my-site/site-detail/:siteID/state/:target/:targetID',
    element: <MySiteArea />,
    loader: mainSiteDetailLoader, //サブメニューのLoader
  },
  //HCCVQグループ詳細（ポップアウト用）
  HCCVQGroupDetailPopOut: {
    path: '/pop-out/my-site/:siteID/:targetID/:unitType',
    element: <HCCVQGroupDetailPopOut />,
    loader: HCCVQGroupDetailLoader,
  },
  //メールアドレス変更
  changeEmail: {
    path: '/pages/my-site/account-setting/change-email',
    element: <ChangeEmail />,
  },
  //パスワード変更
  changePassword: {
    path: '/pages/my-site/account-setting/change-password',
    element: <ChangePassword />,
  },
  //お知らせ設定
  notifications: {
    path: '/pages/my-site/account-setting/notifications',
    element: <Notifications />,
  },
  //ロゴ設定
  displaySetting: {
    path: '/pages/my-site/account-setting/display-setting',
    element: <DisplaySetting />,
    loader: displaySettingLoader,
  },
  //アカウント削除
  deleteAccount: {
    path: '/pages/my-site/account-setting/delete-account',
    element: <DeleteAccount />,
  },
  //サイト追加
  addSite: {
    path: '/pages/my-site/add-site',
    element: <AddSite />,
  },
  //サイト設定
  siteSetting: {
    path: '/pages/my-site/site-detail/:siteID/site-setting',
    element: <SiteSetting />,
    loader: siteSettingLoader,
  },
  //ゲートウェイ一覧
  gwList: {
    path: '/pages/my-site/site-detail/:siteID/gw-setting',
    element: <GWRegistration />,
  },
  //通信系統番号
  systemList: {
    path: '/pages/my-site/site-detail/:siteID/gw-setting/:gwID/communication',
    element: <Communication />,
    loader: communicationLoader,
  },
  //SL通信室外機一覧
  slOUList: {
    path: '/pages/my-site/site-detail/:siteID/gw-setting/:gwID/communication/:systemType/sl-ou-registration',
    element: <OURegistration />,
    loader: ouRegistrationLoader,
  },
  //SL通信室内機一覧
  slIUList: {
    path: '/pages/my-site/site-detail/:siteID/gw-setting/:gwID/communication/:systemType/sl-ou-registration/:ouID/sl-iu-registration',
    element: <IURegistration />,
    loader: iuRegistrationLoader,
  },
  //その他空調機一覧
  slOtherUnitList: {
    path: '/pages/my-site/site-detail/:siteID/gw-setting/:gwID/communication/:systemType/sl-ou-registration/sl-other-registration',
    element: <OtherRegistration />,
    loader: otherRegistrationLoader,
  },
  //熱源機一覧
  rtUnitList: {
    path: '/pages/my-site/site-detail/:siteID/gw-setting/:gwID/communication/:systemType/rt-registration',
    element: <RTRegistration />,
    loader: rtRegistrationLoader,
  },
  //RT通信室内機一覧
  rtIUList: {
    path: '/pages/my-site/site-detail/:siteID/gw-setting/:gwID/communication/:systemType/rt-registration/:ouID/rt-iu-registration',
    element: <RTIURegistration />,
    loader: rtIURegistrationLoader,
  },
  //Modbus通信室外機一覧
  modbusOUList: {
    path: '/pages/my-site/site-detail/:siteID/gw-setting/:gwID/communication/:systemType/modbus-ou-registration',
    element: <ModbusOURegistration />,
    loader: modbusOURegistrationLoader,
  },
  //Modbus通信室内機一覧
  modbusIUList: {
    path: '/pages/my-site/site-detail/:siteID/gw-setting/:gwID/communication/:systemType/modbus-ou-registration/:ouID/modbus-iu-registration',
    element: <ModbusIURegistration />,
    loader: modbusIURegistrationLoader,
  },
  //空調機一括登録差分比較
  collectiveSettingDifference: {
    path: '/pop-out/my-site/site-detail/:siteID/gw-setting/:gwID/collective-setting-difference',
    element: <CollectiveSettingDifference />,
    loader: collectiveSettingDifferenceLoader,
  },
  //フロア一覧
  floorList: {
    path: '/pages/my-site/site-detail/:siteID/floor-detail',
    element: <FloorRegistration />,
  },
  //グループ一覧
  groupList: {
    path: '/pages/my-site/site-detail/:siteID/floor-detail/:floorID/group-registration',
    element: <GroupRegistration />,
  },
  //グループレイアウト
  groupLayout: {
    path: '/pages/my-site/site-detail/:siteID/floor-detail/:floorID/group-registration/:groupID/group-layout-registration',
    element: <GroupLayoutRegistration />,
    loader: groupLayoutRegistrationLoader,
  },
  //セキュリィティ
  security: {
    path: '/pages/my-site/site-detail/:siteID/security',
    element: <SiteSecurity />,
    loader: securityLoader,
  },
  //日報一覧
  dailyReportList: {
    path: '/pages/my-site/site-detail/:siteID/daily-report',
    element: <DailyReport />,
    loader: dailyReportLoader,
  },
  //定期レポート一覧
  periodicReportList: {
    path: '/pages/my-site/site-detail/:siteID/periodic-report',
    element: <PeriodicReport />,
    loader: PeriodicReportLoader,
  },
  //サービスヒストリー機能
  serviceHistory: {
    path: '/pages/my-site/site-detail/:siteID/service-history',
    element: <ServiceHistory />,
    loader: ServiceHistoryLoader,
  },
  //異常不調履歴
  conditionHistory: {
    path: '/pages/my-site/site-detail/:siteID/condition-history',
    element: <ConditionHistory />,
    loader: conditionHistoryLoader,
  },
  //日報要求
  requestReport: {
    path: '/pages/my-site/site-detail/:siteID/request-report',
    element: <RequestReport />,
    loader: requestReportLoader,
  },
  //定期レポート設定
  periodicReportSetting: {
    path: '/pages/my-site/site-detail/:siteID/periodic-report-setting',
    element: <PeriodicReportSetting />,
    loader: periodicReportSettingLoader,
  },
  //ヒストリー機能
  operationDataHistory: {
    path: '/pages/my-site/site-detail/:siteID/operation-data-history',
    element: <OperationDataHistory />,
    loader: operationDataHistoryLoader,
  },
  //Hyozan用グループ一覧
  hyozanGroupList: {
    path: '/pages/my-site/site-detail/:siteID/hyozan-group-registration',
    element: <HCCVQGroupRegistration unitSystem="RT" />,
  },
  //Hyozan用グループレイアウト
  hyozanGroupLayout: {
    path: '/pages/my-site/site-detail/:siteID/hyozan-group-registration/:groupID/hyozan-group-layout-registration',
    element: <ModbusGroupLayoutRegistration unitSystem="RT" />,
    loader: ModbusGroupLayoutRegistrationLoader,
  },
  //Modbus用グループ一覧
  modbusGroupList: {
    path: '/pages/my-site/site-detail/:siteID/modbus-group-registration',
    element: <HCCVQGroupRegistration unitSystem="Modbus" />,
  },
  //Modbus用グループレイアウト
  modbusGroupLayout: {
    path: '/pages/my-site/site-detail/:siteID/modbus-group-registration/:groupID/modbus-group-layout-registration',
    element: <ModbusGroupLayoutRegistration unitSystem="Modbus" />,
    loader: ModbusGroupLayoutRegistrationLoader,
  },
  //スケジュール設定
  scheduleSetting: {
    path: '/pages/my-site/site-detail/:siteID/schedule',
    element: <ScheduleSetting />,
    loader: ScheduleSettingLoader,
  },
  //当日スケジュール設定
  todaySchedule: {
    path: '/pages/my-site/site-detail/:siteID/schedule/today',
    element: <TodaySchedule />,
    loader: TodayScheduleLoader,
  },
  //特定日スケジュール設定
  dailySchedule: {
    path: '/pages/my-site/site-detail/:siteID/schedule/daily',
    element: <DailySchedule />,
    loader: DailyScheduleLoader,
  },
  //デマンド制御
  demandControl: {
    path: '/pages/my-site/site-detail/:siteID/demand',
    element: <DemandControl />,
    loader: DemandLoader,
  },
  //課金設定
  billingSetting: {
    path: '/pages/my-site/site-detail/:siteID/billing-setting',
    element: <BillingSetting />,
  },
  //ロジック
  logic: {
    path: '/pages/my-site/site-detail/:siteID/logic',
    element: <Logic />,
    loader: logicLoader,
  },
  //省エネ制御
  savingEnergyMain: {
    path: '/pages/my-site/site-detail/:siteID/saving-energy',
    element: <SavingEnergyMain />,
    loader: savingEnergyMainLoader,
  },
  //省エネ設定
  savingEnergy: {
    path: '/pages/my-site/site-detail/:siteID/saving-energy-setting',
    element: <SavingEnergyDetail />,
    loader: savingEnergyLoader,
  },
  //ログイン前HELP
  //ユーザ規約
  termsOfService: {
    path: '/pages/help/terms-of-service',
    element: (
      <TermsOfService isAuthenticated={false} isRegisteringUser={false} />
    ),
  },
  //プライバシーポリシー
  privacyPolicy: {
    path: '/pages/help/privacy-policy',
    element: <PrivacyPolicy isAuthenticated={false} />,
  },
  //ログイン後HELP
  //マニュアル
  mainManual: {
    path: '/pages/my-site/help/manual',
    element: <Manual />,
  },
  //ユーザ規約
  mainTermsOfService: {
    path: '/pages/my-site/help/terms-of-service',
    element: (
      <TermsOfService isAuthenticated={true} isRegisteringUser={false} />
    ),
  },
  //プライバシーポリシー
  mainPrivacyPolicy: {
    path: '/pages/my-site/help/privacy-policy',
    element: <PrivacyPolicy isAuthenticated={true} />,
  },
};

/**
 * 各コンポーネントの親子関係
 */
export const router = createBrowserRouter([
  //通常ページ用
  {
    ...routeList.root,
    errorElement: <Navigate to={routeList.login.path} replace={true} />, //パスが違った場合ここに飛ぶ
    children: [
      //未ログインでも閲覧できる画面
      {index: true, element: routeList.login.element},
      {...routeList.login},
      {...routeList.registerAccount},
      {...routeList.registerAccountTermsOfService},
      {...routeList.resetPassword},
      {...routeList.termsOfService},
      {...routeList.privacyPolicy},
      //ログインしないと閲覧できない画面
      {
        path: routeList.mySite.path,
        element: <MainMySite />,
        errorElement: <Navigate to={routeList.mySite.path} replace={true} />, //エラーがスローされた場合ここに飛ぶ,
        children: [
          {
            index: true,
            element: routeList.mySite.element,
          },
          {...routeList.siteDetail},
          {...routeList.targetDetail},
          {...routeList.changeEmail},
          {...routeList.changePassword},
          {...routeList.notifications},
          {...routeList.displaySetting},
          {...routeList.deleteAccount},
          {...routeList.addSite},
          {
            path: routeList.siteDetail2.path,
            element: (
              <>
                <MainSiteDetail fullWidth={true} />
                <Outlet />
              </>
            ),
            loader: mainSiteDetailLoader,
            children: [
              {
                index: true,
                element: routeList.siteDetail2.element,
              },
              {...routeList.siteSetting},
              {
                path: routeList.gwList.path,
                element: <GWMain />,
                loader: gwLoader,
                children: [
                  {
                    index: true,
                    element: routeList.gwList.element,
                  },
                  {...routeList.systemList},
                  {...routeList.slOUList},
                  {...routeList.slIUList},
                  {...routeList.slOtherUnitList},
                  {...routeList.rtUnitList},
                  {...routeList.rtIUList},
                  //　STEP2ではMODBUS通信は無効化されるため一時的にコメントアウト
                  // {...routeList.modbusOUList},
                  // {...routeList.modbusIUList},
                ],
              },
              {
                path: routeList.floorList.path,
                element: <FloorMain />,
                loader: floorMainLoader,
                children: [
                  {
                    index: true,
                    element: routeList.floorList.element,
                  },
                  {...routeList.groupList},
                  {...routeList.groupLayout},
                ],
              },
              {...routeList.security},
              {...routeList.dailyReportList},
              {...routeList.periodicReportList},
              {...routeList.serviceHistory},
              {...routeList.operationDataHistory},
              {...routeList.conditionHistory},
              {...routeList.requestReport},
              {...routeList.periodicReportSetting},
              {
                path: routeList.hyozanGroupList.path,
                element: <HCCVQGroupMain unitSystem="rt" />,
                loader: hccvqGroupRegistrationLoader,
                children: [
                  {
                    index: true,
                    element: routeList.hyozanGroupList.element,
                  },
                  {...routeList.hyozanGroupLayout},
                ],
              },
              //STEP2ではMODBUS通信は無効化されるため一時的にコメントアウト
              // {
              //   path: routeList.modbusGroupList.path,
              //   element: <HCCVQGroupMain unitSystem="modbus" />,
              //   loader: hccvqGroupRegistrationLoader,
              //   children: [
              //     {
              //       index: true,
              //       element: routeList.modbusGroupList.element,
              //     },
              //     {...routeList.modbusGroupLayout},
              //   ],
              // },
              {...routeList.billingSetting},
              {...routeList.scheduleSetting},
              {...routeList.todaySchedule},
              {...routeList.dailySchedule},
              {...routeList.demandControl},
              {...routeList.logic},
              {...routeList.savingEnergyMain},
              {...routeList.savingEnergy},
            ],
          },
          {...routeList.mainManual},
          {...routeList.mainTermsOfService},
          {...routeList.mainPrivacyPolicy},
        ],
      },
    ],
  },
  //ポップアウト用
  {
    ...routeList.popOut,
    errorElement: <Navigate to={routeList.login.path} replace={true} />, //パスが違った場合ここに飛ぶ
    children: [
      {
        path: routeList.HCCVQGroupDetailPopOut.path,
        element: <MainMySite />,
        children: [
          {
            index: true,
            element: routeList.HCCVQGroupDetailPopOut.element,
            loader: routeList.HCCVQGroupDetailPopOut.loader,
          },
        ],
      },
      {
        path: routeList.collectiveSettingDifference.path,
        element: <MainMySite />,
        children: [
          {
            index: true,
            element: routeList.collectiveSettingDifference.element,
            loader: routeList.collectiveSettingDifference.loader,
          },
        ],
      },
    ],
  },
]);
export default router;
